import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid2 } from '@mui/material'
import AssociatedSectorSelect from 'components/forms/specific/AssociatedSectorSelect'
import AssociatedStationSelect from 'components/forms/specific/AssociatedStationSelect'
import SimpleFilterSelect from 'components/forms/specific/SimpleFilterSelect'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import { intersection, isNil, orderBy } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import { getListWithStatus } from 'referencial/util/StatusUtils'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import useBoolean from 'utils/customHook/useBoolean'
import { H_QUALITO_RESEARCH_CRITERIAS } from '../../../account/constants/AccessRulesConstants'
import ProgressCard from '../../../components/card/ProgressCard'
import Checkbox from '../../../components/forms/Checkbox'
import Input from '../../../components/forms/Input'
import SimpleDatePicker from '../../../components/forms/SimpleDatePicker'
import SelectionSelect from '../../../components/forms/specific/SelectionSelect'
import ThresholdSelect from '../../../components/forms/specific/ThresholdSelect'
import Icon from '../../../components/icon/Icon'
import SuivipcAction from '../../../station/components/suivipc/qualitometer/actions/SuivipcAction'
import { STATION_QUALITOMETER_NAMES, STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from '../../../station/constants/StationConstants'
import HabilitationRequired from '../../../utils/components/HabilitationRequired'
import { getDate } from '../../../utils/DateUtil'
import { hasValue } from '../../../utils/NumberUtil'
import { getQualificationSelectOptions, getStatusSelectOptions } from '../../../utils/StatusUtil'
import QualityAction from '../../actions/QualityAction'
import { RESEARCH_TYPE, SELECTION, THRESHOLD } from '../../constants/QualityConstants'
import { thresholdLevels } from '../../constants/QualityResearchConstants'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import useActions from 'utils/customHook/useActions'
import useUpdateEffect from 'utils/customHook/useUpdateEffect'
import AnalysisAction from 'quality/actions/AnalysisAction'
import useAbortController from 'utils/customHook/useAbortController'
import { QUALITY_CAMPAIGN_TYPES_CONSTANTS } from 'campaign/constants/CampaignConstants'
import useSandreList from 'utils/customHook/useSandreList'
import useListIndexed from 'utils/customHook/useListIndexed'
import { CardTable } from 'components/datatable/NewTable'
import ConfirmModal from 'components/modal/ConfirmModal'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { selectedColor } from 'utils/constants/ColorTheme'
import Select from 'components/forms/Select'
import DtoJobParameters from 'import/dto/DtoJobParameters'
import JobAction from 'import/actions/JobAction'

const DialogSavedResearch = ({
    isOpen = false,
    closePopup = () => { },
    savedResearch = {},
    searchFilter = {},
}) => {
    const dispatch = useDispatch()
    const {
        accountUser,
    } = useSelector(store => ({
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const [title, setTitle] = useState(savedResearch.title ?? '')
    const { value: isExport, toggle: toggleExport } = useBoolean(false)
    const [jobParam, setJobParam] = useState({})

    useEffect(() => {
        setTitle(savedResearch.title ?? '')
    }, [savedResearch.title])

    const onClosePopup = useCallback(() => {
        closePopup()
        setTitle('')
    }, [])

    const exportFormat = [
        { id: 'XLSX', label: i18n.exportXLSX },
        { id: 'CSV', label: i18n.exportCSV },
        { id: 'JSON', label: i18n.exportJSON },
    ]

    const slidingPeriods = [
        { id: 'DAY', label: i18n.day },
        { id: 'WEEK', label: i18n.week },
        { id: 'MONTH', label: i18n.month },
    ]

    const onCreateJob = (researchId) => {
        const newJob = {
            jobType: 'qualityResearchStats',
            login: accountUser.login,
            name: `${i18n.exportName} ${title}`,
            parameters: new DtoJobParameters({
                routingKey: 'export.quality.research.stats',
                jobType: 'qualityResearchStats',
                parameters: [JSON.stringify({
                    researchId,
                    ...jobParam,
                })],
            }),
        }
        dispatch(JobAction.addJob(newJob, false)).then((json = { error: true }) => {
            if (!json.error) {
                dispatch(JobAction.executeJob(json.id)).finally(() => {
                    dispatch(ToastrAction.info(i18n.exportedFileCanBeDownloadOnJob))
                    onClosePopup()
                })
            }
        })
    }

    const onSave = () => {
        const {
            researchType,
            ...jsonFilter
        } = searchFilter
        const {
            stations,
            ...rest
        } = jsonFilter

        const parameters = [
            rest.parameters,
            rest.selection && rest.selectionResults || [],
        ].filter(l => !!l.length)
        const allParameters = intersection(...parameters)

        const newSavedResearch = {
            id: -1,
            ...savedResearch,
            title,
            researchType,
            jsonFilter: {
                ...rest,
                allStations: stations?.map(s => s.id),
                allParameters,
            },
            login: accountUser.login,
        }

        const callback = (res) => {
            dispatch(QualityAction.fetchSavedResearchs())
            if (isExport) {
                onCreateJob(res.id)
            } else {
                onClosePopup()
            }
        }
        if (isNil(savedResearch.id)) {
            dispatch(QualityAction.createSavedResearch(newSavedResearch)).then(callback)
        } else {
            dispatch(QualityAction.updateSavedResearch(newSavedResearch)).then(callback)
        }
    }

    return (
        <Dialog
            fullWidth
            maxWidth='md'
            open={isOpen}
        >
            <DefaultDialogTitle
                title={i18n.saveResearch}
                onClose={() => {
                    setTitle('')
                    closePopup()
                }}
            />
            <DialogContent>
                <Card>
                    <CardContent>
                        {searchFilter.researchType === RESEARCH_TYPE.WATER && (
                            <Grid2 container spacing={1} alignItems='center'>
                                <Grid2 size={8}>
                                    <Input
                                        title={i18n.title}
                                        value={title}
                                        onChange={setTitle}
                                    />
                                </Grid2>
                                <Grid2 size={4}>
                                    <Checkbox
                                        label={i18n.createAutomaticExport}
                                        checked={isExport}
                                        onChange={toggleExport}
                                    />
                                </Grid2>
                                {isExport && (
                                    <>
                                        <Grid2 size={3}>
                                            <Select
                                                label={i18n.slidingPeriod}
                                                options={slidingPeriods}
                                                value={jobParam.slidingPeriod}
                                                onChange={v => setJobParam(p => ({ ...p, slidingPeriod: v }))}
                                                disabled
                                            />
                                        </Grid2>
                                        <Grid2 size={3}>
                                            <Select
                                                label={i18n.exportFormat}
                                                options={exportFormat}
                                                value={jobParam.format}
                                                noNullValue
                                                obligatory
                                                onChange={v => setJobParam(p => ({ ...p, format: v }))}
                                            />
                                        </Grid2>
                                        <Grid2 size={6}>
                                            <Input
                                                title={i18n.selectDirectory}
                                                value={jobParam.repository}
                                                obligatory
                                                onChange={v => setJobParam(p => ({ ...p, repository: v }))}
                                            />
                                        </Grid2>
                                    </>
                                )}
                            </Grid2>
                        )}
                        {searchFilter.researchType !== RESEARCH_TYPE.WATER && (
                            <Input
                                title={i18n.title}
                                value={title}
                                onChange={setTitle}
                            />
                        )}
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Grid2 container justifyContent='flex-end' alignItems='flex-end'>
                    <Grid2 container justifyContent='flex-end'>
                        <Button
                            onClick={onSave}
                            disabled={!title}
                            variant='contained'
                        >
                            {i18n.validate}
                        </Button>
                    </Grid2>
                </Grid2>
            </DialogActions>
        </Dialog>
    )
}

DialogSavedResearch.propTypes = {
    isOpen: PropTypes.bool,
    closePopup: PropTypes.func,
    savedResearch: PropTypes.shape({}),
    searchFilter: PropTypes.shape({}),
}

const getResearchTypes = () => [
    { value: RESEARCH_TYPE.WATER, label: i18n.waterReaserchType },
    { value: RESEARCH_TYPE.INDICES, label: i18n.indices },
    { value: RESEARCH_TYPE.STATES, label: i18n.states },
    { value: RESEARCH_TYPE.TAXON, label: i18n.taxons },
]

const SearchCriteria = ({
    defaultFilter = {},
    savedResearch = {},
    setSavedResearch = () => { },
    onSearchIndices = () => { },
    onSearchStates = () => { },
    onSearchHydrobios = () => { },
    onSearchAnalysis = () => { },
}) => {
    const dispatch = useDispatch()
    const {
        qualitometers,
        citiesIndex,
        uniqPoints,
        networks,
        parametersProps,
        unitsProps,
        fractions,
        methods,
        supports,
        producersList,
        laboratoriesList,
        determinators,
        parameterGroupUsage,
        installations,
        distributionUnits,
        productionUnits,
        qualityCampaigns,
        taxonsProps,

        usedParameters,
        usedSupports,
        usedFractions,
        usedUnits,
        usedMethods,
        usedIndices,
        usedTaxons,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        citiesIndex: store.CityReducer.citiesIndex,
        uniqPoints: store.QualityReducer.uniqPoints,
        networks: store.NetworkReducer.networks,
        parametersProps: store.ParameterReducer.parameters,
        unitsProps: store.UnitReducer.units,
        fractions: store.FractionReducer.fractions,
        methods: store.MethodReducer.methods,
        supports: store.SupportReducer.supports,
        producersList: store.ContributorReducer.producers,
        laboratoriesList: store.ContributorReducer.laboratories,
        determinators: store.ContributorReducer.determinators,
        parameterGroupUsage: store.ParameterReducer.parameterGroupUsage,
        installations: store.InstallationReducer.installationsLight,
        distributionUnits: store.DistributionUnitReducer.distributionUnits,
        productionUnits: store.ProductionUnitReducer.productionUnits,
        qualityCampaigns: store.QualityReducer.qualityCampaigns,
        taxonsProps: store.TaxonReducer.taxons,

        usedParameters: store.QualityReducer.usedParameters,
        usedSupports: store.QualityReducer.usedSupports,
        usedFractions: store.QualityReducer.usedFractions,
        usedUnits: store.QualityReducer.usedUnits,
        usedMethods: store.QualityReducer.usedMethods,
        usedIndices: store.QualityReducer.usedIndices,
        usedTaxons: store.QualityReducer.usedTaxons,
    }), shallowEqual)

    const indexedSupports = useListIndexed(supports, 'code')
    const indexedParameters = useListIndexed(parametersProps, 'code')
    const indexedFractions = useListIndexed(fractions, 'code')
    const indexedUnits = useListIndexed(unitsProps, 'code')
    const indexedMethods = useListIndexed(methods, 'code')
    const indexedTaxons = useListIndexed(taxonsProps, 'code')

    const supportsList = useMemo(() => {
        return usedSupports.map(code => indexedSupports[code]).filter(s => !!s)
    }, [indexedSupports, usedSupports])
    const parametersList = useMemo(() => {
        return usedParameters.map(code => indexedParameters[code]).filter(s => !!s)
    }, [indexedParameters, usedParameters])
    const fractionsList = useMemo(() => {
        return usedFractions.map(code => indexedFractions[code]).filter(s => !!s)
    }, [indexedFractions, usedFractions])
    const unitsList = useMemo(() => {
        return usedUnits.map(code => indexedUnits[code]).filter(s => !!s)
    }, [indexedUnits, usedUnits])
    const methodsList = useMemo(() => {
        return usedMethods.map(code => indexedMethods[code]).filter(s => !!s)
    }, [indexedMethods, usedMethods])
    const indicesList = useMemo(() => {
        return usedIndices.map(code => indexedParameters[code]).filter(s => !!s)
    }, [indexedParameters, usedIndices])
    const taxonsList = useMemo(() => {
        return usedTaxons.map(code => indexedTaxons[code]).filter(s => !!s)
    }, [indexedTaxons, usedTaxons])

    const {
        value: isOpen,
        setTrue: openPopun,
        setFalse: closePopup,
    } = useBoolean(false)

    const [researchType, setResearchType] = useState(defaultFilter.researchType)
    const [stationType, setStationType] = useState(defaultFilter.stationType)
    const [startDate, setStartDate] = useState(defaultFilter.startDate)
    const [endDate, setEndDate] = useState(defaultFilter.endDate)
    const [qualitometerIds, setQualitometerIds] = useState(defaultFilter.qualitometerIds)
    const [point, setPoint] = useState(defaultFilter.point)
    const [network, setNetwork] = useState(defaultFilter.network)
    const [operationNetwork, setOperationNetwork] = useState(defaultFilter.operationNetwork)
    const [filter, setFilter] = useState(defaultFilter.filter)
    const [filterResults, setFilterResults] = useState([])
    const [campaign, setCampaign] = useState(defaultFilter.campaign)

    const [sector, setSector] = useState(defaultFilter.sector)
    const [sectorAssociatedStations, setSectorAssociatedStations] = useState([])
    const [installation, setInstallation] = useState(defaultFilter.installation)
    const [installationAssociatedStations, setInstallationAssociatedStations] = useState([])
    const [productionUnit, setProductionUnit] = useState(defaultFilter.productionUnit)
    const [prodAssociatedStations, setProdAssociatedStations] = useState([])
    const [distributionUnit, setDistributionUnit] = useState(defaultFilter.distributionUnit)
    const [distriAssociatedStations, setDistriAssociatedStations] = useState([])

    const [producers, setProducers] = useState(defaultFilter.producers)
    const [laboratories, setLaboratories] = useState(defaultFilter.laboratories)
    const [support, setSupport] = useState(defaultFilter.support)
    const [method, setMethod] = useState(defaultFilter.method)
    const [selection, setSelection] = useState(defaultFilter.selection)
    const [selectionResults, setSelectionResults] = useState(defaultFilter.selectionResults)
    const [groupCode, setGroupCode] = useState(defaultFilter.groupCode)
    const [taxonLevels, setTaxonLevels] = useState(defaultFilter.taxonLevels)
    const [parameters, setParameters] = useState(defaultFilter.parameters)
    const [units, setUnits] = useState(defaultFilter.units)
    const [fraction, setFraction] = useState(defaultFilter.fraction)
    const [threshold, setThreshold] = useState(defaultFilter.threshold)
    const [thresholdLevel, setThresholdLevel] = useState(defaultFilter.thresholdLevel)
    const [status, setStatus] = useState(defaultFilter.status)
    const [qualification, setQualification] = useState(defaultFilter.qualification)
    const [quantificationControl, setQuantificationControl] = useState(defaultFilter.quantificationControl)
    const [displayAdvancedStatistics, setDisplayAdvancedStatistics] = useState(defaultFilter.displayAdvancedStatistics)
    const [determiners, setDeterminers] = useState(defaultFilter.determiners)
    const [taxons, setTaxons] = useState(defaultFilter.taxons)
    const [phases, setPhases] = useState(defaultFilter.phases)

    const taxonLevelsList = useSandreList('TAXONS.NIVEAUX')
    const taxonPhases = useSandreList('PRELEVEMENTS.PHASE')

    const researchTypes = useMemo(getResearchTypes, [])

    const stationsTypeLabel = useMemo(() => {
        const stationsTypes = STATION_QUALITOMETER_NAMES.filter(s => qualitometers.some(q => q.stationType === `${s.code}`))
        return orderBy(stationsTypes, 'name', 'asc')
    }, [qualitometers])

    const groupsFormated = useMemo(() => {
        const stationTypes = hasValue(stationType) ? [STATION_QUALITOMETER_NAMES.find(s => s.code === stationType)] : STATION_QUALITOMETER_NAMES
        return stationTypes.map(({ id, code, name }) => {
            const childrens = qualitometers.filter(q => q.stationType == code)
            return {
                id,
                code,
                name,
                elements: childrens.map(c => ({ id: c.id, code: c.code, name: c.name })),
            }
        })
    }, [qualitometers, stationType])

    const qualitometersFormated = useMemo(() => {
        const qualitometersFiltered = hasValue(stationType) ? qualitometers.filter(q => q.stationType === `${stationType}`) : qualitometers
        return qualitometersFiltered.map(q => {
            const city = citiesIndex[q.townCode]
            const name = q.name || ''
            const nameFormatted = `${q.code ? `${name} [${q.code}]` : name} ${city?.name ? ` - ${city.name}` : ''}`
            return {
                ...q,
                name: nameFormatted,
            }
        })
    }, [citiesIndex, qualitometers, stationType])

    const parametersFormated = useMemo(() => {
        return getListWithStatus(parametersList)
    }, [parametersList])

    const indicesFormated = useMemo(() => {
        return getListWithStatus(indicesList)
    }, [indicesList])

    const unitsFormated = useMemo(() => {
        return getListWithStatus(unitsList)
    }, [unitsList])

    const statusList = useMemo(() => getStatusSelectOptions(), [])

    const qualificationsList = useMemo(() => getQualificationSelectOptions(), [])

    const filteredQualityCampaigns = useMemo(() => researchType === RESEARCH_TYPE.TAXON ? qualityCampaigns.filter(qc => qc.campaignType === QUALITY_CAMPAIGN_TYPES_CONSTANTS.HYDROBIO) : qualityCampaigns, [qualityCampaigns, researchType])

    const getStations = () => {
        if (prodAssociatedStations?.length || distriAssociatedStations?.length || sectorAssociatedStations?.length || installationAssociatedStations?.length || (!isNil(filter) && filter !== -1) || qualitometerIds?.length) {
            const ids = [
                prodAssociatedStations,
                distriAssociatedStations,
                sectorAssociatedStations,
                installationAssociatedStations,
                qualitometerIds,
                !isNil(filter) && filter !== -1 ? filterResults.map(s => s.id) : [],
            ].filter(f => !!f?.length)
            const listIds = intersection(...ids)
            const filteredQualitometers = qualitometers.filter(s => listIds.find(id => id === s.id))
            return !isNil(stationType) ? filteredQualitometers.filter(s => s.stationType == stationType) : filteredQualitometers
        }
        return !isNil(stationType) ? qualitometers.filter(s => s.stationType == stationType) : qualitometers
    }

    const searchFilter = {
        researchType,
        stationType,
        startDate,
        endDate,
        qualitometerIds,
        point,
        network,
        operationNetwork,
        filter,
        // filterResults,
        campaign,

        sector,
        // sectorAssociatedStations,
        installation,
        // installationAssociatedStations,
        productionUnit,
        // prodAssociatedStations,
        distributionUnit,
        // distriAssociatedStations,


        stations: getStations(),

        producers: producers ?? [],
        laboratories: laboratories ?? [],
        determiners: determiners ?? [],
        taxons: taxons ?? [],
        taxonLevels: taxonLevels ?? [],
        phases: phases ?? [],
        support,
        method,
        selection: selection && selection !== '-1' ? selection : undefined,
        selectionResults: selection && selection !== '-1' ? selectionResults : undefined,
        groupCode,
        parameters: parameters ?? [],
        units: units ?? [],
        fraction,
        threshold,
        thresholdLevel,
        status,
        qualification,
        quantificationControl,
        displayAdvancedStatistics,
    }

    const onStartResearch = () => {
        if (!hasValue(researchType)) {
            dispatch(ToastrAction.info(i18n.searchCriteriasMessage))
            return
        }
        const {
            stations,
            ...allFilter
        } = searchFilter
        switch (researchType) {
            case RESEARCH_TYPE.INDICES:
                onSearchIndices({ ...allFilter, stations: stations.map(s => s.code), stationsIds: stations.map(s => s.id) })
                break
            case RESEARCH_TYPE.STATES:
                onSearchStates({ ...allFilter, stations: stations.map(s => s.code), stationsIds: stations.map(s => s.id) })
                break
            case RESEARCH_TYPE.TAXON:
                onSearchHydrobios({ ...allFilter, stations: stations.map(s => s.id) })
                break
            default:
                onSearchAnalysis({ ...allFilter, stations: stations.map(s => s.id) })
                break
        }
    }

    useUpdateEffect(() => {
        setResearchType(defaultFilter.researchType)
        setStationType(defaultFilter.stationType)
        setStartDate(defaultFilter.startDate)
        setEndDate(defaultFilter.endDate)
        setQualitometerIds(defaultFilter.qualitometerIds)
        setPoint(defaultFilter.point)
        setNetwork(defaultFilter.network)
        setOperationNetwork(defaultFilter.operationNetwork)
        setFilter(defaultFilter.filter)
        setCampaign(defaultFilter.campaign)
        // setFilterResults([])
        setSector(defaultFilter.sector)
        // setSectorAssociatedStations([])
        setInstallation(defaultFilter.installation)
        // setInstallationAssociatedStations([])
        setProductionUnit(defaultFilter.productionUnit)
        // setProdAssociatedStations([])
        setDistributionUnit(defaultFilter.distributionUnit)
        // setDistriAssociatedStations([])
        setProducers(defaultFilter.producers)
        setLaboratories(defaultFilter.laboratories)
        setDeterminers(defaultFilter.determiners)
        setTaxons(defaultFilter.taxons)
        setTaxonLevels(defaultFilter.taxonLevels)
        setPhases(defaultFilter.phases)
        setSupport(defaultFilter.support)
        setMethod(defaultFilter.method)
        setSelection(defaultFilter.selection)
        // setSelectionResults([])
        setGroupCode(defaultFilter.groupCode)
        setParameters(defaultFilter.parameters)
        setUnits(defaultFilter.units)
        setFraction(defaultFilter.fraction)
        setThreshold(defaultFilter.threshold)
        setThresholdLevel(defaultFilter.thresholdLevel)
        setStatus(defaultFilter.status)
        setQualification(defaultFilter.qualification)
        setQuantificationControl(defaultFilter.quantificationControl)
        setDisplayAdvancedStatistics(defaultFilter.displayAdvancedStatistics)
    }, [defaultFilter])

    useActions(() => ({
        save: openPopun,
        clear: () => {
            setSavedResearch()
            setResearchType(RESEARCH_TYPE.WATER)
            setStationType()
            setStartDate()
            setEndDate()
            setQualitometerIds()
            setPoint()
            setNetwork()
            setOperationNetwork()
            setFilter(-1)
            setFilterResults([])
            setCampaign()
            setSector()
            setSectorAssociatedStations([])
            setInstallation()
            setInstallationAssociatedStations([])
            setProductionUnit()
            setProdAssociatedStations([])
            setDistributionUnit()
            setDistriAssociatedStations([])
            setProducers([])
            setLaboratories([])
            setDeterminers([])
            setTaxons([])
            setPhases()
            setSupport()
            setMethod()
            setSelection('-1')
            setSelectionResults([])
            setGroupCode()
            setParameters([])
            setUnits([])
            setFraction()
            setThreshold()
            setThresholdLevel()
            setStatus()
            setQualification()
            setQuantificationControl()
            setDisplayAdvancedStatistics()
        },
    }), [])

    const thresholdType = useMemo(() => {
        switch (researchType) {
            case RESEARCH_TYPE.TAXON: return THRESHOLD.TAXON
            case RESEARCH_TYPE.INDICES: return THRESHOLD.INDICE
            default: return THRESHOLD.PC
        }
    }, [researchType])

    return (
        <Card>
            <CardContent>
                <Grid2 container columnSpacing={2}>
                    <Grid2 size={2}>
                        <SuperMultiAutocomplete
                            options={researchTypes}
                            label={i18n.researchType}
                            keyLabel={'label'}
                            keyValue={'value'}
                            onChange={setResearchType}
                            values={researchType}
                        />
                    </Grid2>
                    <Grid2 size={2}>
                        <SimpleDatePicker
                            id='startDate'
                            label={i18n.startDate}
                            value={startDate}
                            max={endDate}
                            onChange={setStartDate}
                        />
                    </Grid2>
                    <Grid2 size={2}>
                        <SimpleDatePicker
                            id='endDate'
                            label={i18n.endDate}
                            value={endDate}
                            onChange={setEndDate}
                            min={startDate}
                        />
                    </Grid2>
                    <Grid2 size={6} />
                    <Grid2 size={6}>
                        <SimpleFilterSelect
                            stationType={STATION_TYPE_NAME.quality}
                            onChange={(res, f) => {
                                setFilter(f)
                                setFilterResults(res)
                            }}
                            value={filter}
                            stations={qualitometers}
                        />
                    </Grid2>
                    <Grid2 size={6} />
                    <Grid2 size={2}>
                        <SuperMultiAutocomplete
                            options={stationsTypeLabel}
                            label={i18n.stationTypeLabel}
                            keyValue='code'
                            onChange={setStationType}
                            values={stationType}
                        />
                    </Grid2>
                    <Grid2 size={4}>
                        <SuperMultiAutocomplete
                            options={qualitometersFormated}
                            optionsGroups={groupsFormated}
                            values={qualitometerIds}
                            label={i18n.watchpoints}
                            onChange={setQualitometerIds}
                            multiple
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <SuperMultiAutocomplete
                            options={uniqPoints}
                            values={point}
                            label={i18n.samplePoint}
                            keyValue='identifiant'
                            displayWithCode
                            onChange={setPoint}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <SuperMultiAutocomplete
                            label={i18n.network}
                            options={networks}
                            keyLabel='nameWithSandre'
                            values={network}
                            onChange={setNetwork}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        {
                            (researchType === RESEARCH_TYPE.WATER || researchType === RESEARCH_TYPE.TAXON) && (
                                <SuperMultiAutocomplete
                                    label={i18n.campaign}
                                    options={filteredQualityCampaigns}
                                    values={campaign}
                                    onChange={setCampaign}
                                />
                            )
                        }
                    </Grid2>
                    <HabilitationRequired habilitation={H_QUALITO_RESEARCH_CRITERIAS}>
                        <Grid2 size={12}>
                            <StyledFieldSet>
                                <StyledLegend>{'AEP'}</StyledLegend>
                                <Grid2 columnSpacing={2} container>
                                    <Grid2 size={6}>
                                        <AssociatedSectorSelect
                                            value={sector}
                                            onChange={(list, value) => {
                                                setSector(value)
                                                setSectorAssociatedStations(list)
                                            }}
                                            stationTypeReturn={STATION_TYPE_CONSTANT.QUALITY}
                                        />
                                    </Grid2>
                                    <Grid2 size={6}>
                                        <AssociatedStationSelect
                                            label={i18n.installation}
                                            value={installation}
                                            options={installations}
                                            onChange={(list, value) => {
                                                setInstallation(value)
                                                setInstallationAssociatedStations(list)
                                            }}
                                            stationType={STATION_TYPE_CONSTANT.INSTALLATION}
                                            stationTypeReturn={STATION_TYPE_CONSTANT.QUALITY}
                                            keyValue='code'
                                            displayWithCode
                                        />
                                    </Grid2>
                                    <Grid2 size={6}>
                                        <AssociatedStationSelect
                                            label={i18n.productionUnit}
                                            value={productionUnit}
                                            options={productionUnits}
                                            onChange={(list, value) => {
                                                setProductionUnit(value)
                                                setProdAssociatedStations(list)
                                            }}
                                            stationType={STATION_TYPE_CONSTANT.PRODUCTION}
                                            stationTypeReturn={STATION_TYPE_CONSTANT.QUALITY}
                                            keyValue='code'
                                            displayWithCode
                                        />
                                    </Grid2>
                                    <Grid2 size={6}>
                                        <AssociatedStationSelect
                                            label={i18n.distributionUnit}
                                            value={distributionUnit}
                                            options={distributionUnits}
                                            onChange={(list, value) => {
                                                setDistributionUnit(value)
                                                setDistriAssociatedStations(list)
                                            }}
                                            stationType={STATION_TYPE_CONSTANT.DISTRIBUTION}
                                            stationTypeReturn={STATION_TYPE_CONSTANT.QUALITY}
                                            keyValue='code'
                                            displayWithCode
                                        />
                                    </Grid2>
                                </Grid2>
                            </StyledFieldSet>
                        </Grid2>
                    </HabilitationRequired>
                    {
                        ([RESEARCH_TYPE.WATER, RESEARCH_TYPE.INDICES, RESEARCH_TYPE.TAXON].includes(researchType)) && (
                            <>
                                <Grid2 size={12}>
                                    <StyledFieldSet>
                                        <StyledLegend>{i18n.sample}</StyledLegend>
                                        <Grid2 columnSpacing={2} container>
                                            <Grid2 size={6}>
                                                <MultiContributorsAutocomplete
                                                    label={i18n.producer}
                                                    options={producersList}
                                                    onChange={setProducers}
                                                    values={producers}
                                                    multiple
                                                />
                                            </Grid2>
                                            {
                                                researchType === RESEARCH_TYPE.WATER && (
                                                    <Grid2 size={6}>
                                                        <MultiContributorsAutocomplete
                                                            label={i18n.laboratory}
                                                            options={laboratoriesList}
                                                            values={laboratories}
                                                            onChange={setLaboratories}
                                                            multiple
                                                        />
                                                    </Grid2>
                                                )
                                            }
                                            {
                                                researchType === RESEARCH_TYPE.TAXON && (
                                                    <Grid2 size={6}>
                                                        <MultiContributorsAutocomplete
                                                            label={i18n.determiner}
                                                            options={determinators}
                                                            values={determiners}
                                                            onChange={setDeterminers}
                                                            displayWithCode
                                                            multiple
                                                            limit={2}
                                                        />
                                                    </Grid2>
                                                )
                                            }
                                            {
                                                researchType !== RESEARCH_TYPE.STATES && (
                                                    <Grid2 size={6}>
                                                        <SuperMultiAutocomplete
                                                            label={i18n.support}
                                                            options={supportsList}
                                                            values={support}
                                                            onChange={setSupport}
                                                        />
                                                    </Grid2>
                                                )
                                            }
                                            <Grid2 size={6}>
                                                <SuperMultiAutocomplete
                                                    label={i18n.method}
                                                    options={methodsList}
                                                    values={method}
                                                    onChange={setMethod}
                                                />
                                            </Grid2>
                                        </Grid2>
                                    </StyledFieldSet>
                                </Grid2>
                                <Grid2 size={12}>
                                    <StyledFieldSet>
                                        <StyledLegend>{researchType === RESEARCH_TYPE.TAXON ? i18n.hydrobiology : i18n.physicochemical}</StyledLegend>
                                        <Grid2 columnSpacing={2} container>
                                            <Grid2 size={(researchType === RESEARCH_TYPE.WATER || researchType === RESEARCH_TYPE.TAXON) ? 4 : 12}>
                                                <SelectionSelect
                                                    value={selection}
                                                    onChange={(res, s) => {
                                                        setSelection(s)
                                                        setSelectionResults(res)
                                                    }}
                                                    type={researchType === -3 ? SELECTION.TAXON : SELECTION.PC}
                                                />
                                            </Grid2>
                                            {
                                                researchType === RESEARCH_TYPE.WATER && (
                                                    <Grid2 size={4}>
                                                        <SuperMultiAutocomplete
                                                            label={i18n.group}
                                                            options={parameterGroupUsage}
                                                            values={groupCode}
                                                            onChange={setGroupCode}
                                                            keyValue='groupCode'
                                                            keyLabel='labelWithCode'
                                                        />
                                                    </Grid2>
                                                )
                                            }
                                            {
                                                researchType === RESEARCH_TYPE.WATER && (
                                                    <Grid2 size={4}>
                                                        <SuperMultiAutocomplete
                                                            label={i18n.operationNetwork}
                                                            options={networks}
                                                            keyLabel='nameWithSandre'
                                                            values={operationNetwork}
                                                            onChange={setOperationNetwork}
                                                        />
                                                    </Grid2>
                                                )
                                            }
                                            {
                                                researchType === RESEARCH_TYPE.TAXON && (
                                                    <Grid2 size={6}>
                                                        <SuperMultiAutocomplete
                                                            label={i18n.taxonsLevel}
                                                            options={taxonLevelsList}
                                                            values={taxonLevels}
                                                            onChange={setTaxonLevels}
                                                            keyValue='code'
                                                            multiple
                                                        />
                                                    </Grid2>
                                                )
                                            }
                                            {
                                                researchType === RESEARCH_TYPE.TAXON ? (
                                                    <>
                                                        <Grid2 size={6}>
                                                            <SuperMultiAutocomplete
                                                                label={i18n.taxons}
                                                                options={taxonsList}
                                                                limit={2}
                                                                keyValue='code'
                                                                onChange={setTaxons}
                                                                values={taxons}
                                                                keyLabel='labelWithCode'
                                                                additionalKeysFilter={['name', 'code', 'latinName']}
                                                                multiple
                                                            />
                                                        </Grid2>
                                                        <Grid2 size={6}>
                                                            <SuperMultiAutocomplete
                                                                label={i18n.phases}
                                                                options={taxonPhases}
                                                                keyValue='code'
                                                                onChange={setPhases}
                                                                values={phases}
                                                                multiple
                                                                limit={4}
                                                            />
                                                        </Grid2>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Grid2 size={6}>
                                                            {researchType === RESEARCH_TYPE.WATER && (
                                                                <SuperMultiAutocomplete
                                                                    label={i18n.parameters}
                                                                    options={parametersFormated}
                                                                    limit={1}
                                                                    keyValue='code'
                                                                    onChange={setParameters}
                                                                    values={parameters}
                                                                    keyLabel='labelWithCode'
                                                                    additionalKeysFilter={['name', 'code', 'shortLabel']}
                                                                    multiple
                                                                />
                                                            )}
                                                            {researchType === RESEARCH_TYPE.INDICES && (
                                                                <SuperMultiAutocomplete
                                                                    label={i18n.parameters}
                                                                    options={indicesFormated}
                                                                    limit={1}
                                                                    keyValue='code'
                                                                    onChange={setParameters}
                                                                    values={parameters}
                                                                    keyLabel='labelWithCode'
                                                                    additionalKeysFilter={['name', 'code', 'shortLabel']}
                                                                    multiple
                                                                />
                                                            )}
                                                        </Grid2>
                                                        <Grid2 size={3}>
                                                            <SuperMultiAutocomplete
                                                                label={i18n.unit}
                                                                options={unitsFormated}
                                                                keyValue='code'
                                                                onChange={setUnits}
                                                                values={units}
                                                                keyLabel='symbolWithCode'
                                                                multiple
                                                            />
                                                        </Grid2>
                                                        <Grid2 size={3}>
                                                            <SuperMultiAutocomplete
                                                                label={i18n.fraction}
                                                                options={fractionsList}
                                                                keyValue='id'
                                                                onChange={setFraction}
                                                                values={fraction}
                                                                keyLabel='labelWithCode'
                                                            />
                                                        </Grid2>
                                                    </>
                                                )
                                            }
                                            <Grid2 size={3}>
                                                <ThresholdSelect
                                                    selected={threshold}
                                                    onChange={setThreshold}
                                                    thresholdType={thresholdType}
                                                />
                                            </Grid2>
                                            <Grid2 size={3}>
                                                <SuperMultiAutocomplete
                                                    options={thresholdLevels}
                                                    keyValue='code'
                                                    label={i18n.thresholdLevel}
                                                    onChange={setThresholdLevel}
                                                    values={thresholdLevel}
                                                />
                                            </Grid2>
                                            <Grid2 size={3}>
                                                <SuperMultiAutocomplete
                                                    options={statusList}
                                                    label={i18n.status}
                                                    onChange={setStatus}
                                                    values={status}
                                                    noSort
                                                />
                                            </Grid2>
                                            <Grid2 size={3}>
                                                <SuperMultiAutocomplete
                                                    options={qualificationsList}
                                                    label={i18n.qualification}
                                                    onChange={setQualification}
                                                    values={qualification}
                                                    noSort
                                                />
                                            </Grid2>
                                        </Grid2>
                                    </StyledFieldSet>
                                </Grid2>
                            </>
                        )
                    }
                    <Grid2 container size={12} style={{ margin: '10px' }} justifyContent='space-between'>
                        {researchType === RESEARCH_TYPE.WATER ? (
                            <>
                                <Grid2 size='auto' display='flex'>
                                    <Checkbox
                                        label={i18n.quantificationControl}
                                        onChange={setQuantificationControl}
                                        checked={quantificationControl}
                                    />
                                    <Checkbox
                                        label={i18n.displayAdvancedStatistics}
                                        onChange={setDisplayAdvancedStatistics}
                                        checked={displayAdvancedStatistics}
                                        componentClassName='padding-left-2'
                                    />
                                </Grid2>
                            </>
                        ) : (
                            <Grid2 size={10} />
                        )}
                        <Grid2 size='auto'>
                            <Button
                                onClick={onStartResearch}
                                variant='contained'
                            >
                                <Icon icon='search' />
                                <span style={{ marginLeft: 5 }}>
                                    {i18n.search}
                                </span>
                            </Button>
                        </Grid2>
                    </Grid2>
                </Grid2>
            </CardContent>
            <DialogSavedResearch
                isOpen={isOpen}
                closePopup={closePopup}
                savedResearch={savedResearch}
                searchFilter={searchFilter}
            />
        </Card>
    )
}

SearchCriteria.propTypes = {
    defaultFilter: PropTypes.shape({}),
    savedResearch: PropTypes.shape({}),
    setSavedResearch: PropTypes.func,
    onSearchIndices: PropTypes.func,
    onSearchStates: PropTypes.func,
    onSearchHydrobios: PropTypes.func,
    onSearchAnalysis: PropTypes.func,
}

const TableSavedResearch = ({
    onSelectSavedCriterias = () => { },
    savedResearch = {},
    setSavedResearch = () => { },
}) => {
    const dispatch = useDispatch()
    const {
        savedResearchs,
        parameters,
        thresholds,
        selections,
    } = useSelector(store => ({
        savedResearchs: store.QualityReducer.savedResearchs,
        parameters: store.ParameterReducer.parameters,
        thresholds: store.QualityReducer.thresholds,
        selections: store.ParameterReducer.selections,
    }), shallowEqual)

    const indexedParameters = useListIndexed(parameters, 'code')

    const researchTypes = useMemo(getResearchTypes, [])

    const {
        value: isConfirmOpen,
        setTrue: onOpenConfirm,
        setFalse: onCloseConfirm,
    } = useBoolean(false)
    const [selectedId, setSelectedId] = useState()

    useEffect(() => {
        dispatch(QualityAction.fetchSavedResearchs())
    }, [dispatch])

    const findThreshold = useCallback((researchType, thresholdId) => {
        switch (researchType) {
            case RESEARCH_TYPE.INDICES: return thresholds.find(t => t.thresholdType === THRESHOLD.INDICE && t.id === thresholdId)
            case RESEARCH_TYPE.WATER: return thresholds.find(t => t.thresholdType === THRESHOLD.PC && t.id === thresholdId)
            case RESEARCH_TYPE.TAXON: return thresholds.find(t => t.thresholdType === THRESHOLD.TAXON && t.id === thresholdId)
            case RESEARCH_TYPE.STATES: return thresholds.find(t => t.thresholdType === THRESHOLD.STATE && t.id === thresholdId)
            default: return undefined
        }
    }, [thresholds])

    const formattedSavedResearchs = useMemo(() => {
        return orderBy(savedResearchs, 'login').map(sr => {
            return {
                id: sr.id,
                researchType: researchTypes.find(rt => rt.value === sr.researchType)?.label ?? '',
                title: sr.title,
                startDate: getDate(sr.jsonFilter.startDate),
                endDate: getDate(sr.jsonFilter.endDate),
                selection: selections.find(s => s.id === sr.jsonFilter.selectionCode)?.name ?? '',
                listData: sr.jsonFilter.parameters?.map(code => indexedParameters[code]?.name).join(', '),
                threshold: findThreshold(sr.researchType, sr.jsonFilter.threshold)?.name ?? '',

                colorLine: savedResearch.id === sr.id ? selectedColor : undefined,
            }
        })
    }, [findThreshold, indexedParameters, researchTypes, savedResearch.id, savedResearchs, selections])

    return (
        <>
            <CardTable
                title={i18n.savedResearchCriterias}

                rows={formattedSavedResearchs}
                headers={['researchType', 'title', 'startDate', 'endDate', 'selection', { key: 'listData', value: `${i18n.parameters}/${i18n.taxons}` }, 'threshold']}
                lineActions={[
                    {
                        icon: 'clear',
                        onClick: ({ id }) => {
                            onOpenConfirm()
                            setSelectedId(id)
                        },
                    },
                    {
                        icon: 'reply',
                        onClick: ({ id }) => {
                            const filter = savedResearchs.find(sr => sr.id === id)
                            onSelectSavedCriterias(filter)
                            setSavedResearch(filter)
                        },
                    },
                ]}
            />
            <ConfirmModal
                isOpen={isConfirmOpen}
                title={i18n.deletingConfirmation}
                onValidate={() => {
                    dispatch(QualityAction.deleteSavedResearch(selectedId)).finally(() => {
                        dispatch(QualityAction.fetchSavedResearchs())
                    })
                    onCloseConfirm()
                    setSavedResearch()
                    setSelectedId()
                }}
                onClose={onCloseConfirm}
            />
        </>
    )
}

TableSavedResearch.propTypes = {
    onSelectSavedCriterias: PropTypes.func,
    savedResearch: PropTypes.shape({}),
    setSavedResearch: PropTypes.func,
}

const SearchCriteriaPanel = ({
    defaultFilter,
    setFilter = () => { },
    onSelectSavedCriterias = () => { },
}) => {
    const dispatch = useDispatch()
    const {
        taxons,
    } = useSelector(store => ({
        taxons: store.TaxonReducer.taxons,
    }), shallowEqual)

    const indexedTaxons = useListIndexed(taxons, 'code')

    const [researchType, setResearchType] = useState(defaultFilter?.researchType)
    const [selectedSavedResearch, setSelectedSavedResearch] = useState()

    const {
        value: isSearching,
        setTrue: setSearchingToTrue,
        setFalse: setSearchingToFalse,
    } = useBoolean(false)
    const [searchProgress, setSearchProgress] = useState(0)

    const {
        controllerRef,
        initController,
    } = useAbortController()

    const onSearchIndices = searchFilter => {
        setFilter(searchFilter)
        setResearchType(searchFilter.researchType)
        setSearchingToTrue()
        setSearchProgress(0)

        const parameters = [
            searchFilter.parameters,
            searchFilter.selection && searchFilter.selectionResults || [],
        ].filter(l => !!l.length)
        const intersectParams = intersection(...parameters)

        const filter = {
            ...searchFilter,
            parameters: intersectParams.length ? intersectParams : undefined, // Option[Seq[String]] = None,
            units: searchFilter.units?.length ? searchFilter.units : undefined, // Option[Seq[String]] = None,
            producers: searchFilter.producers?.length ? searchFilter.producers : undefined, // Option[Seq[Int]] = None,
        }
        dispatch(QualityAction.fetchSearchIndices(filter)).finally(setSearchingToFalse)
    }

    const onSearchStates = searchFilter => {
        setFilter(searchFilter)
        setResearchType(searchFilter.researchType)
        setSearchingToTrue()
        setSearchProgress(0)
        dispatch(QualityAction.fetchSearchStates(searchFilter)).finally(setSearchingToFalse)
    }

    const onSearchHydrobios = searchFilter => {
        setFilter(searchFilter)
        setResearchType(searchFilter.researchType)
        setSearchingToTrue()
        setSearchProgress(0)
        if (hasValue(searchFilter.threshold) && searchFilter.threshold !== '') {
            dispatch(SuivipcAction.fetchQualityThresholds(searchFilter.threshold))
        }
        if (controllerRef.current.signal.aborted) {
            initController()
        }

        const listTaxons = [
            searchFilter.taxons,
            searchFilter.selection && searchFilter.selectionResults || [],
        ].filter(l => !!l.length)
        const intersectTaxons = intersection(...listTaxons)

        const taxonsByLevels = searchFilter.taxonLevels?.length > 0 ? intersectTaxons.filter(t => searchFilter.taxonLevels.includes(indexedTaxons[t]?.level)) : intersectTaxons

        const filter = {
            lightMode: true, // Option[Boolean] = None,

            stations: searchFilter.stations, // Option[Seq[Int]] = None
            support: searchFilter.support, // Option[String] = None,
            units: searchFilter.units?.length ? searchFilter.units : undefined, // Option[Seq[String]] = None,
            method: searchFilter.method, // Option[String] = None,
            producers: searchFilter.producers?.length ? searchFilter.producers : undefined, // Option[Seq[Int]] = None,
            laboratories: searchFilter.laboratories?.length ? searchFilter.laboratories : undefined, // Option[Seq[Int]] = None,
            determiners: searchFilter.determiners?.length ? searchFilter.determiners : undefined, // Option[Seq[Int]] = None,
            taxons: taxonsByLevels.length ? taxonsByLevels : undefined, // Option[Seq[String]] = None,
            phases: searchFilter.phases?.length ? searchFilter.phases : undefined, // Option[Seq[Int]] = None,
            quantificationControl: searchFilter.quantificationControl, // Option[Boolean] = None,
            network: searchFilter.network, // Option[String] = None,
            operationNetwork: searchFilter.operationNetwork, // Option[String] = None,
            status: searchFilter.status, // Option[String] = None,
            qualification: searchFilter.qualification, // Option[String] = None,
            startDate: searchFilter.startDate, // Option[DateTime] = None,
            endDate: searchFilter.endDate, // Option[DateTime] = None,
            point: searchFilter.point, // Option[String] = None,
            groupCode: searchFilter.groupCode, // Option[Int] = None,
            campaign: searchFilter.campaign, // Option[Double] = None,
        }

        setFilter(searchFilter)
        dispatch(QualityAction.fetchSearchHydrobios(filter, controllerRef.current.signal, setSearchProgress))
            .finally(setSearchingToFalse)
    }

    const onSearchAnalysis = searchFilter => {
        setResearchType(searchFilter.researchType)
        setSearchingToTrue()
        setSearchProgress(0)
        if (hasValue(searchFilter.threshold) && searchFilter.threshold !== '') {
            dispatch(SuivipcAction.fetchQualityThresholds(searchFilter.threshold))
        }
        if (controllerRef.current.signal.aborted) {
            initController()
        }

        const parameters = [
            searchFilter.parameters,
            searchFilter.selection && searchFilter.selectionResults || [],
        ].filter(l => !!l.length)
        const intersectParams = intersection(...parameters)

        const filter = {
            lightMode: true, // Option[Boolean] = None,

            stations: searchFilter.stations, // Option[Seq[Int]] = None
            support: searchFilter.support, // Option[String] = None,
            parameters: intersectParams.length ? intersectParams : undefined, // Option[Seq[String]] = None,
            units: searchFilter.units?.length ? searchFilter.units : undefined, // Option[Seq[String]] = None,
            fraction: searchFilter.fraction, // Option[String] = None,
            method: searchFilter.method, // Option[String] = None,
            producers: searchFilter.producers?.length ? searchFilter.producers : undefined, // Option[Seq[Int]] = None,
            laboratories: searchFilter.laboratories?.length ? searchFilter.laboratories : undefined, // Option[Seq[Int]] = None,
            determiners: searchFilter.determiners?.length ? searchFilter.determiners : undefined, // Option[Seq[Int]] = None,
            quantificationControl: searchFilter.quantificationControl, // Option[Boolean] = None,
            network: searchFilter.network, // Option[String] = None,
            operationNetwork: searchFilter.operationNetwork, // Option[String] = None,
            status: searchFilter.status, // Option[String] = None,
            qualification: searchFilter.qualification, // Option[String] = None,
            startDate: searchFilter.startDate, // Option[DateTime] = None,
            endDate: searchFilter.endDate, // Option[DateTime] = None,
            point: searchFilter.point, // Option[String] = None,
            groupCode: searchFilter.groupCode, // Option[Int] = None,
            campaign: searchFilter.campaign, // Option[Double] = None,
        }
        setFilter(searchFilter)

        dispatch(AnalysisAction.fetchAnalysis(filter, controllerRef.current.signal, setSearchProgress))
            .finally(setSearchingToFalse)
    }

    return (
        <Grid2 container spacing={1} sx={{ paddingBottom: '100px' }}>
            <Grid2 size={12}>
                <SearchCriteria
                    defaultFilter={defaultFilter}
                    savedResearch={selectedSavedResearch}
                    setSavedResearch={setSelectedSavedResearch}
                    onSearchIndices={onSearchIndices}
                    onSearchStates={onSearchStates}
                    onSearchHydrobios={onSearchHydrobios}
                    onSearchAnalysis={onSearchAnalysis}
                />
            </Grid2>
            {
                isSearching && (
                    <Grid2 size={12}>
                        <Card>
                            <CardContent>
                                <ProgressCard progress={searchProgress} withoutCard />
                                {
                                    researchType === RESEARCH_TYPE.WATER && (
                                        <div style={{ paddingBottom: 10, textAlign: 'center' }}>
                                            <Button
                                                onClick={() => controllerRef.current.abort()}
                                                variant='contained'
                                            >
                                                {i18n.cancel}
                                            </Button>
                                        </div>
                                    )
                                }
                            </CardContent>
                        </Card>
                    </Grid2>
                )
            }
            <Grid2 size={12}>
                <TableSavedResearch
                    onSelectSavedCriterias={onSelectSavedCriterias}
                    savedResearch={selectedSavedResearch}
                    setSavedResearch={setSelectedSavedResearch}
                />
            </Grid2>
        </Grid2>
    )
}

SearchCriteriaPanel.propTypes = {
    defaultFilter: PropTypes.shape({
        researchType: PropTypes.number,
        // ...
    }),
    setFilter: PropTypes.func,
    onSelectSavedCriterias: PropTypes.func,
    onSearchResultsReceived: PropTypes.func,
}

export default SearchCriteriaPanel
